"use client";

import Image from "next/image";
import useTheme from "src/context/appThemeContext";
import React, { useEffect, useState } from "react";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";
import TychoButton from "src/components/atoms/buttons/TychoButton";

const PagesServicesSection = ({ data }) => {
  const { subtitle = "OUR WORK", title = "Apps We're Proud Of" } = data || {};

  const apps = data?.projects?.liveProjects || data?.apps || [];

  const { theme, mounted } = useTheme();

  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCards, setVisibleCards] = useState(4);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      let cards = 1;

      if (width >= 1536) {
        cards = 4;
      } else if (width >= 1440) {
        cards = 3;
      } else if (width >= 768) {
        cards = 3;
      }

      setVisibleCards(cards);

      const maxIndex = Math.max(0, apps.length - cards);

      setCurrentIndex((prev) => (prev > maxIndex ? maxIndex : prev));
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, [apps.length]);

  const maxIndex = Math.max(0, apps.length - visibleCards);

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  const getCardWidth = () => {
    return `calc((100% - ${(visibleCards - 1) * 24}px) / ${visibleCards})`;
  };

  return (
    <section className=" mb-10! overflow-hidden mt-10!">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6! mb-10!">
        <div>
          {subtitle && (
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600 text-xs font-bold uppercase tracking-widest block mb-3!">
              {subtitle}
            </span>
          )}

          {title && <h2 className="text-2xl lg:text-5xl font-bold tracking-tight text-text-primary">{title}</h2>}
        </div>

        <TychoButton
          size="lg"
          variant="secondary"
          href="/success-stories"
          title="View All Projects"
          target="_self"
          titleSize="16px"
          className="px-4! py-2! text-sm! self-end w-full md:w-[170px]"
        />
      </div>

      {apps.length > 0 && (
        <div className="relative w-full overflow-hidden">
          {apps.length > visibleCards && (
            <>
              <button
                onClick={prevSlide}
                aria-label="Previous"
                className="  absolute left-0  top-[240px] -translate-y-1/2 z-30 flex items-center  justify-center w-12  h-12 rounded-full border border-border-primary  bg-surface-primary  shadow-md  cursor-pointer  text-text-primary "
              >
                <LuChevronLeft size={24} />
              </button>

              <button
                onClick={nextSlide}
                aria-label="Next"
                className=" absolute right-0  top-[240px] -translate-y-1/2 z-30 flex  items-center justify-center w-12 h-12 rounded-full border border-border-primary  bg-surface-primary  shadow-md cursor-pointer  text-text-primary  "
              >
                <LuChevronRight size={24} />
              </button>
            </>
          )}

          <div className="overflow-hidden">
            <div
              className="flex gap-6 transition-transform duration-500 ease-in-out"
              style={{
                transform: `translateX(calc(-${currentIndex} * (${getCardWidth()} + 24px)))`,
              }}
            >
              {apps.map((app, idx) => {
                const banner = app.bannerImage || app.image;

                const technologies = app.technologies?.slice(0, 5) || [];

                return (
                  <div
                    key={idx}
                    className="
                      shrink-0
                      rounded-2xl
                      border
                      border-border-primary
                      overflow-hidden
                      flex
                      flex-col
                      justify-between
                      min-h-[520px]
                      group
                    "
                    style={{
                      width: getCardWidth(),
                      background: "var(--card-background)",
                      boxShadow: "var(--card-shadow)",
                    }}
                  >
                    <div className="relative h-[240px] overflow-hidden border-b border-border-primary/40">
                      <div className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-500 flex items-center justify-center text-white text-xs font-bold">
                        {String(idx + 1).padStart(2, "0")}
                      </div>

                      <Image src={banner} alt={app.title} fill />

                      {mounted && theme === "dark" && <div className="absolute inset-0 bg-gradient-to-t from-surface-primary via-black/10 to-transparent" />}
                    </div>

                    <div className="p-5! flex flex-col flex-1 justify-between">
                      <div>
                        <div className="flex items-center justify-between">
                          <h3 className="text-xl font-bold text-text-primary">{app.title}</h3>

                          {app.year && <span className="text-sm font-semibold text-text-primary">{app.year}</span>}
                        </div>

                        <div className="flex flex-wrap gap-2! mt-3!">
                          {app.category && (
                            <span className="bg-violet-500/10 text-violet-400 text-[10px] font-bold uppercase tracking-wider px-2.5! py-1! rounded-full border border-violet-500/20">
                              {app.category}
                            </span>
                          )}

                          {app.subCategory && (
                            <span className="bg-fuchsia-500/10 text-fuchsia-400 text-[10px] font-bold uppercase tracking-wider px-2.5! py-1! rounded-full border border-fuchsia-500/20">
                              {app.subCategory}
                            </span>
                          )}
                        </div>

                        <div className="mt-5!">
                          <h4 className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600 text-sm font-semibold uppercase mb-3!">
                            Project Description
                          </h4>

                          <p className="text-text-secondary text-sm leading-5 line-clamp-3 h-[60px] overflow-hidden">{app.description}</p>
                        </div>
                      </div>

                      <div className="flex flex-col lg:flex-row justify-between items-end mt-5!">
                        <div className="self-start">
                          {technologies.length > 0 && (
                            <>
                              <h4 className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-purple-600 dark:from-cyan-400 dark:to-purple-600 text-sm font-semibold uppercase mb-3!">
                                Technology
                              </h4>

                              <div className="flex flex-wrap gap-3!">
                                {technologies.map((tech, i) => (
                                  <Image key={i} src={tech.icon} alt={tech.name} width={22} height={22} />
                                ))}
                              </div>
                            </>
                          )}
                        </div>

                        <TychoButton
                          size="sm"
                          variant="secondary"
                          href={app.websiteUrl || app.website}
                          title="Explore More"
                          target="_blank"
                          titleSize="16px"
                          className="px-3! py-2! mt-5! lg:mt-0!"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PagesServicesSection;
